// FILE: src/support/AdminTickets.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { auth, db } from "../firebase";
import SuperAdminNav from "../components/SuperAdminNav";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";

function tsMillis(x) {
  if (!x) return 0;
  if (typeof x.toMillis === "function") return x.toMillis();
  if (x.seconds != null) return x.seconds * 1000;
  if (x instanceof Date) return x.getTime();
  return 0;
}
function tsToLocal(x) {
  const ms = tsMillis(x);
  return ms ? new Date(ms).toLocaleString() : "—";
}

function normalizeTicketStatus(ticketOrStatus) {
  const t = typeof ticketOrStatus === "object" ? ticketOrStatus : null;
  const raw = String(t ? t.status : ticketOrStatus || "").toLowerCase();
  let k = raw;
  if (k === "live") k = "open";
  if (k === "solved") k = "closed";
  if (k === "pending" || k === "replied") k = "awaiting_customer";
  if (!["open", "awaiting_support", "awaiting_customer", "closed"].includes(k)) k = "open";

  // If older tickets only have open/closed, infer awaiting state from lastReplyRole.
  if (k === "open" && t) {
    const r = String(t.lastReplyRole || "").toLowerCase();
    if (r === "staff" || r === "support" || r === "admin") return "awaiting_customer";
    if (r === "user") return "awaiting_support";
  }
  return k;
}

function statusLabel(k) {
  if (k === "awaiting_support") return "Awaiting Support";
  if (k === "awaiting_customer") return "Awaiting Customer";
  if (k === "closed") return "Closed";
  return "Open";
}

function bestTs(t) {
  return t?.lastReplyAt || t?.lastMessageAt || t?.updatedAt || t?.createdAt || null;
}

export default function AdminTickets() {
  const location = useLocation();
  const { tenantId = "" } = useParams();
  const isTenantScoped = location.pathname.startsWith("/tenant/") && location.pathname.endsWith("/support");

  const [isAdmin, setIsAdmin] = useState(false);
  const [canManageTenantTickets, setCanManageTenantTickets] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Modal state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewTicket, setViewTicket] = useState(null); // full doc with _coll
  const [savingStatus, setSavingStatus] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  // Conversation state
  const [messages, setMessages] = useState([]);
  const [replyDraft, setReplyDraft] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyInfo, setReplyInfo] = useState("");
  const msgUnsubRef = useRef(null);
  const msgEndRef = useRef(null);

  // confirm admin / tenant-admin access
  useEffect(() => {
    const unsub = auth.onIdTokenChanged(async (u) => {
      if (!u) {
        setIsAdmin(false);
        setCanManageTenantTickets(false);
        return;
      }
      try {
        const tok = await u.getIdTokenResult(true);
        let profile = {};
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          profile = snap.exists() ? (snap.data() || {}) : {};
        } catch {}

        const platformAdmin =
          !!tok?.claims?.admin ||
          !!profile.isAdmin ||
          profile.role === "platform_admin";

        console.log("AdminTickets auth debug", {
          uid: u.uid,
          email: u.email || "",
          claims: tok?.claims || {},
          profile,
          isTenantScoped,
          tenantId,
          platformAdmin
        });

        setIsAdmin(platformAdmin);

        if (isTenantScoped && tenantId) {
          if (platformAdmin) {
            setCanManageTenantTickets(true);
            return;
          }

          try {
            const memberSnap = await getDoc(doc(db, "tenants", tenantId, "members", u.uid));
            const member = memberSnap.exists() ? (memberSnap.data() || {}) : {};
            const role = String(member.role || "").toLowerCase();
            setCanManageTenantTickets(["owner", "hr_admin"].includes(role));
          } catch {
            setCanManageTenantTickets(false);
          }
        } else {
          setCanManageTenantTickets(false);
        }
      } catch {
        setIsAdmin(false);
        setCanManageTenantTickets(false);
      }
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [isTenantScoped, tenantId]);

  // Subscribe to platform-wide or tenant-scoped tickets
  useEffect(() => {
    const allowed = isTenantScoped ? canManageTenantTickets : isAdmin;
    if (!allowed) return;

    setLoading(true);
    setErr("");

    const index = new Map(); // key: `${source}:${id}` => row
    const apply = (source) => (snap) => {
      let changed = false;
      snap.forEach((d) => {
        const val = { id: d.id, source, ...d.data() };
        const key = `${source}:${d.id}`;
        index.set(key, val);
        changed = true;
      });
      if (changed) {
        const weight = (r) => {
          const k = normalizeTicketStatus(r);
          if (k === "awaiting_support") return 0;
          if (k === "awaiting_customer") return 1;
          if (k === "open") return 2;
          return 3; // closed
        };
        const all = Array.from(index.values()).sort((a, b) => {
          const dw = weight(a) - weight(b);
          if (dw) return dw;
          return tsMillis(bestTs(b)) - tsMillis(bestTs(a));
        });
        setRows(all);
        setLoading(false);
      }
    };
    const onErr = (e) => {
      setErr(e?.message || String(e));
      setLoading(false);
    };

    if (isTenantScoped && tenantId) {
      const unsub1 = onSnapshot(
        query(collection(db, "tickets"), where("tenantId", "==", tenantId)),
        apply("tickets"),
        onErr
      );
      return () => {
        unsub1 && unsub1();
      };
    }

    const unsub1 = onSnapshot(collection(db, "tickets"), apply("tickets"), onErr);
    const unsub2 = onSnapshot(
      collection(db, "supportTickets"),
      apply("supportTickets"),
      onErr
    );

    return () => {
      unsub1 && unsub1();
      unsub2 && unsub2();
    };
  }, [isAdmin, canManageTenantTickets, isTenantScoped, tenantId]);

  const tickets = useMemo(
    () =>
      rows.map((r) => ({
        id: r.id,
        ticketNumber: r.ticketNumber ?? r.number ?? null,
        tenantId: r.tenantId || "",
        tenantName: r.tenantName || "",
        name: r.name || "",
        email: r.email || "",
        phone: r.phone || "",
        feature: r.feature || "—",
        priority: r.priority || "normal",
        statusKey: normalizeTicketStatus(r),
        lastReplyAt: bestTs(r),
        lastReplyRole: r.lastReplyRole || null,
        createdAt: r.createdAt || null,
        updatedAt: r.updatedAt || null,
        source: r.source,
        attachments: r.attachments || [],
      })),
    [rows]
  );

  function stopMessageListener() {
    try {
      if (typeof msgUnsubRef.current === "function") msgUnsubRef.current();
    } catch (_) {}
    msgUnsubRef.current = null;
  }

  async function openViewModal(id) {
    setViewLoading(true);
    setViewOpen(true);
    setViewTicket(null);
    setMessages([]);
    setReplyDraft("");
    setReplyInfo("");
    stopMessageListener();

    const tryDoc = async (coll, id2) => {
      const snap = await getDoc(doc(db, coll, id2));
      return snap.exists() ? { id: snap.id, ...snap.data(), _coll: coll } : null;
    };
    let full = await tryDoc("tickets", id);
    if (!full) full = await tryDoc("supportTickets", id);

    setViewTicket(full);
    setNotesDraft(full?.notes || "");

    // Live thread
    if (full) {
      const coll = full._coll || "tickets";
      const mq = query(
        collection(db, coll, String(full.id), "messages"),
        orderBy("createdAt", "asc")
      );
      msgUnsubRef.current = onSnapshot(
        mq,
        (snap) => {
          const rows2 = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setMessages(rows2);
          setTimeout(() => {
            msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 0);
        },
        () => {
          // If thread doesn't exist yet, just show empty.
          setMessages([]);
        }
      );
    }

    setViewLoading(false);
  }

  function closeViewModal() {
    stopMessageListener();
    setViewOpen(false);
    setViewTicket(null);
    setMessages([]);
    setReplyDraft("");
    setReplyInfo("");
  }

  async function handleStatusChange(e) {
    if (!viewTicket) return;
    const newStatus = normalizeTicketStatus(e.target.value); // open | pending | closed
    try {
      setSavingStatus(true);
      const coll = viewTicket._coll || "tickets";
      await updateDoc(doc(db, coll, viewTicket.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      setViewTicket((v) => (v ? { ...v, status: newStatus } : v));
      setRows((prev) =>
        prev.map((r) => (r.id === viewTicket.id ? { ...r, status: newStatus } : r))
      );
    } catch (e2) {
      alert("Failed to update status: " + (e2?.message || e2));
    } finally {
      setSavingStatus(false);
    }
  }

  async function saveNotes() {
    if (!viewTicket) return;
    try {
      const coll = viewTicket._coll || "tickets";
      await updateDoc(doc(db, coll, viewTicket.id), {
        notes: notesDraft || "",
        updatedAt: serverTimestamp(),
      });
      setViewTicket((v) => (v ? { ...v, notes: notesDraft || "" } : v));
    } catch (e) {
      alert("Failed to save notes: " + (e?.message || e));
    }
  }

  async function sendReply() {
    if (!viewTicket) return;
    const body = String(replyDraft || "").trim();
    if (!body) return;

    // Use a deterministic message id so we can optimistic-render safely without duplicates.
    const messageId =
      typeof crypto !== "undefined" && crypto?.randomUUID
        ? crypto.randomUUID()
        : `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    try {
      setSendingReply(true);
      setReplyInfo("");

      const me = auth.currentUser;
      if (!me) throw new Error("not_authenticated");

      // Optimistic append so the thread updates instantly even if snapshots are delayed.
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          senderId: me.uid,
          senderRole: "staff",
          body,
          createdAt: new Date(),
          _optimistic: true,
        },
      ]);

      const token = await me.getIdToken();

      const res = await fetch("/api/support/admin/reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ticketId: viewTicket.id,
          message: body,
          messageId,
          // default status is handled server-side; can be overridden if needed
        }),
      });

      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out?.error || "reply_failed");

      setReplyDraft("");
      setReplyInfo("Reply sent to customer (and stored on the ticket).");
    } catch (e) {
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setReplyInfo("Failed to send reply: " + (e?.message || e));
    } finally {
      setSendingReply(false);
    }
  }

  function StatusPill({ statusKey }) {
    const k = normalizeTicketStatus(statusKey);
    const style = {
      display: "inline-block",
      lineHeight: "20px",
      height: 20,
      padding: "0 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      border: "1px solid",
      textTransform: "lowercase",
    };
    if (k === "closed") {
      style.background = "#ecfdf5";
      style.color = "#065f46";
      style.borderColor = "#a7f3d0";
    } else if (k === "awaiting_customer") {
      style.background = "#fff7ed";
      style.color = "#9a3412";
      style.borderColor = "#fed7aa";
    } else {
      style.background = "#eff6ff";
      style.color = "#1e40af";
      style.borderColor = "#bfdbfe";
    }
    return <span style={style}>{statusLabel(k)}</span>;
  }

  const hasAccess = isTenantScoped ? canManageTenantTickets : isAdmin;

  return (
    <div className="ad ad-wrap">
      {isTenantScoped ? (
        <div className="ad-header">
          <h1>Tenant Support</h1>
          <nav className="ad-tabs" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link className="ad-tab" to={`/tenant/${tenantId}`}>
              ← Back to tenant
            </Link>
            <Link className="ad-tab" to={`/tenant/${tenantId}`}>
              Admin Dashboard
            </Link>
            <Link className="ad-tab" to="/">
              Back to App
            </Link>
          </nav>
        </div>
      ) : (
        <SuperAdminNav title="Admin" active="support" />
      )}

      {!hasAccess ? (
        <div className="ad-banner ad-banner--error">
          {isTenantScoped ? "Tenant admin access only." : <>Admin access only. Your account lacks the <code>admin</code> claim.</>}
        </div>
      ) : null}

      {err ? <div className="ad-banner ad-banner--error">{err}</div> : null}

      <section>
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead>
              <tr>
                <th>Ticket</th>
                {!isTenantScoped ? <th>Tenant</th> : null}
                <th>User</th>
                <th>Feature</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Last reply</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="ad-loading" colSpan={isTenantScoped ? 8 : 9}>
                    Loading…
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td className="ad-empty" colSpan={isTenantScoped ? 8 : 9}>
                    No tickets yet.
                  </td>
                </tr>
              ) : (
                tickets.map((t) => (
                  <tr key={`${t.source}:${t.id}`}>
                    <td data-label="Ticket">#{t.ticketNumber ?? "—"}</td>
                    {!isTenantScoped ? (
                      <td data-label="Tenant" className="ad-break">
                        {t.tenantName || t.tenantId || "Direct / B2C"}
                      </td>
                    ) : null}
                    <td data-label="User" className="ad-break">
                      {t.name ? `${t.name} · ` : ""}
                      {t.email || "—"}
                      {t.phone ? ` · ${t.phone}` : ""}
                    </td>
                    <td data-label="Feature">{t.feature}</td>
                    <td data-label="Priority">{t.priority}</td>
                    <td data-label="Status">
                      <StatusPill statusKey={t.statusKey} />
                    </td>
                    <td data-label="Last reply">{tsToLocal(t.lastReplyAt)}</td>
                    <td data-label="Created">{tsToLocal(t.createdAt)}</td>
                    <td className="ad-actions">
                      <button
                        className="ad-btn ad-btn--primary"
                        onClick={() => openViewModal(t.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal */}
      {viewOpen && (
        <div className="ad-modal" onClick={closeViewModal}>
          <div className="ad-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modal-head">
              <div className="t">
                {viewTicket?.ticketNumber
                  ? `Ticket #${viewTicket.ticketNumber}`
                  : "Ticket"}
              </div>
              <button className="x" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="ad-modal-body">
              {viewLoading ? (
                <div>Loading…</div>
              ) : viewTicket ? (
                <div className="kv-grid">
                  <div className="kv">
                    <div className="k">Issue</div>
                    <div className="v">{viewTicket.issue || "—"}</div>
                  </div>
                  <div className="kv">
                    <div className="k">Feature</div>
                    <div className="v">{viewTicket.feature || "—"}</div>
                  </div>
                  <div className="kv">
                    <div className="k">Priority</div>
                    <div className="v">{viewTicket.priority || "normal"}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Tenant</div>
                    <div className="v">{viewTicket.tenantName || viewTicket.tenantId || "Direct / B2C"}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Name</div>
                    <div className="v">{viewTicket.name || "—"}</div>
                  </div>
                  <div className="kv">
                    <div className="k">Email</div>
                    <div className="v">{viewTicket.email || "—"}</div>
                  </div>
                  <div className="kv">
                    <div className="k">Phone</div>
                    <div className="v">{viewTicket.phone || "—"}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Status</div>
                    <div className="v">
                      <select
                        value={normalizeTicketStatus(viewTicket)}
                        onChange={handleStatusChange}
                        disabled={savingStatus}
                        style={{
                          padding: "8px 10px",
                          border: "1px solid #d6d6e7",
                          borderRadius: 8,
                        }}
                        aria-label="Update ticket status"
                      >
                        <option value="open">Open</option>
                        <option value="awaiting_support">Awaiting Support</option>
                        <option value="awaiting_customer">Awaiting Customer</option>
                        <option value="closed">Closed</option>
                      </select>
                      {savingStatus && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#6b7280",
                          }}
                        >
                          Saving…
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="kv">
                    <div className="k">Last reply</div>
                    <div className="v">
                      {(() => {
                        const ts = bestTs(viewTicket);
                        const txt = tsToLocal(ts);
                        const lr = String(viewTicket.lastReplyRole || "").toLowerCase();
                        const from = lr === "staff" || lr === "support" || lr === "admin" ? "Support" : (lr === "user" ? "Customer" : "—");
                        return `${from} · ${txt}`;
                      })()}
                    </div>
                  </div>

                  <div className="kv">
                    <div className="k">Created</div>
                    <div className="v">{tsToLocal(viewTicket.createdAt)}</div>
                  </div>

                  <div className="kv col">
                    <div className="k">Description</div>
                    <div className="v" style={{ whiteSpace: "pre-wrap" }}>
                      {viewTicket.description || "—"}
                    </div>
                  </div>

                  {/* Attachments */}
                  {Array.isArray(viewTicket.attachments) &&
                    viewTicket.attachments.length > 0 && (
                      <div className="kv col">
                        <div className="k">Attachments</div>
                        <div className="v">
                          <div className="att-grid">
                            {viewTicket.attachments.map((a, idx) => (
                              <a
                                key={idx}
                                href={a.url}
                                target="_blank"
                                rel="noreferrer"
                                className="att"
                              >
                                <img
                                  src={a.url}
                                  alt={a.name || `attachment-${idx}`}
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Conversation */}
                  <div className="kv col">
                    <div className="k">Conversation</div>
                    <div className="v">
                      <div className="ad-thread">
                        {messages.length === 0 ? (
                          <div className="ad-thread-empty">
                            No messages yet.
                          </div>
                        ) : (
                          messages.map((m) => {
                            const role = m.senderRole === "staff" ? "Support" : "User";
                            return (
                              <div
                                key={m.id}
                                className={
                                  "ad-msg " +
                                  (m.senderRole === "staff"
                                    ? "ad-msg--staff"
                                    : "ad-msg--user")
                                }
                              >
                                <div className="ad-msg-meta">
                                  <span className="ad-msg-role">{role}</span>
                                  <span className="ad-msg-dot">•</span>
                                  <span className="ad-msg-time">
                                    {tsToLocal(m.createdAt)}
                                  </span>
                                </div>
                                <div className="ad-msg-body">
                                  {String(m.body || "")} 
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={msgEndRef} />
                      </div>

                      <div className="ad-reply">
                        <textarea
                          value={replyDraft}
                          onChange={(e) => setReplyDraft(e.target.value)}
                          rows={4}
                          placeholder="Type a reply to the customer…"
                        />
                        <div className="ad-reply-actions">
                          <button
                            className="ad-btn ad-btn--primary"
                            onClick={sendReply}
                            disabled={sendingReply || !String(replyDraft).trim()}
                          >
                            {sendingReply ? "Sending…" : "Send reply"}
                          </button>
                        </div>
                        {replyInfo ? (
                          <div className="ad-reply-info">{replyInfo}</div>
                        ) : null}
                        <div className="ad-reply-hint">
                          This will: (1) email the customer, and (2) store the reply on the ticket.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="kv col">
                    <div className="k">Notes</div>
                    <div className="v">
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        rows={5}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d6d6e7",
                          borderRadius: 8,
                          resize: "vertical",
                        }}
                        placeholder="Add solution notes or internal context (client can see these)."
                      />
                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          gap: 8,
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          className="ad-btn ad-btn--primary"
                          onClick={saveNotes}
                        >
                          Save notes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>Ticket not found.</div>
              )}
            </div>
            <div className="ad-modal-foot">
              <button className="ad-btn" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <AdStyles />
    </div>
  );
}

function AdStyles() {
  return (
    <style>{`
      .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
      .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      .ad-header{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:8px 0 16px}
      .ad-tabs{display:flex;gap:8px;flex-wrap:wrap}
      .ad-tab{padding:10px 14px;border:1px solid #d6d6e7;border-radius:10px;background:#fff;cursor:pointer;line-height:1;text-decoration:none;color:inherit}
      .ad-tab.is-active{background:#111827;color:#fff;border-color:#111827}

      .ad-banner{padding:10px 12px;border-radius:10px;margin:8px 0;border:1px solid}
      .ad-banner--error{background:#fee2e2;color:#7a0b0b;border-color:#fecaca}

      .ad-table-wrap{width:100%;overflow:auto}
      .ad-table{width:100%;border-collapse:separate;border-spacing:0;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden}
      .ad-table thead th{background:#f9fafb;text-align:left;font-weight:600;padding:12px;border-bottom:1px solid #e5e7eb;white-space:nowrap}
      .ad-table td{padding:12px;border-top:1px solid #f3f4f6;vertical-align:middle}
      .ad-actions{text-align:right;white-space:nowrap}
      .ad-loading,.ad-empty{padding:14px;text-align:center}
      .ad-break{word-break:break-word;overflow-wrap:anywhere}

      @media (max-width: 720px){
        .ad-table thead{display:none}
        .ad-table tr{display:block;border-bottom:1px solid #f3f4f6}
        .ad-table td{display:flex;justify-content:space-between;gap:12px}
        .ad-table td::before{content:attr(data-label);font-weight:600;color:#6b7280}
        .ad-actions{justify-content:flex-end}
      }
      .ad-btn{height:38px;padding:0 14px;border-radius:10px;border:1px solid #cfcfe6;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
      .ad-btn--primary{background:#4f46e5;color:#fff;border-color:#4f46e5}

      /* Modal */
      .ad-modal{position:fixed;inset:0;display:grid;place-items:center;background:rgba(0,0,0,.45);z-index:1000}
      .ad-modal-card{width:min(900px,92vw);background:#fff;border-radius:12px;border:1px solid #ececf5;overflow:hidden}
      .ad-modal-head{display:flex;align-items:center;justify-content:space-between;padding:12px;border-bottom:1px solid #ececf5}
      .ad-modal-head .t{font-weight:800;color:#2a0516}
      .ad-modal-body{padding:12px;display:grid;gap:8px}
      .kv-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
      .kv{display:grid;grid-template-columns:160px 1fr;gap:8px;align-items:center}
      .kv.col{grid-template-columns:1fr}
      .k{font-size:12px;color:#657899;font-weight:700}
      .v{color:#2a0516}
      .ad-modal-foot{display:flex;justify-content:flex-end;gap:8px;padding:12px;border-top:1px solid #ececf5}
      .x{width:36px;height:36px;border-radius:10px;border:1px solid #ececf5;background:#fff;cursor:pointer}

      /* Attachments */
      .att-grid{display:flex;gap:8px;flex-wrap:wrap}
      .att{display:block;width:96px;height:96px;border:1px solid #ececf5;border-radius:8px;overflow:hidden;background:#fafafa}
      .att img{width:100%;height:100%;object-fit:cover;display:block}

      /* Conversation */
      .ad-thread{border:1px solid #ececf5;border-radius:12px;background:#fff;max-height:280px;overflow:auto;padding:10px}
      .ad-thread-empty{color:#6b7280;font-size:13px;padding:8px}
      .ad-msg{padding:10px;border-radius:12px;border:1px solid #f1f1f7;margin-bottom:8px}
      .ad-msg--staff{background:#f5f3ff;border-color:#e9d5ff}
      .ad-msg--user{background:#f9fafb}
      .ad-msg-meta{display:flex;gap:8px;align-items:center;color:#6b7280;font-size:12px;margin-bottom:6px}
      .ad-msg-role{font-weight:800;color:#111827}
      .ad-msg-dot{opacity:.6}
      .ad-msg-body{white-space:pre-wrap;line-height:1.45}

      .ad-reply{margin-top:10px}
      .ad-reply textarea{width:100%;padding:10px;border:1px solid #d6d6e7;border-radius:10px;resize:vertical}
      .ad-reply-actions{display:flex;justify-content:flex-end;margin-top:8px}
      .ad-reply-info{margin-top:8px;font-size:12px;color:#111827;font-weight:700}
      .ad-reply-hint{margin-top:6px;font-size:12px;color:#6b7280}

      @media(max-width:720px){ .kv-grid{grid-template-columns:1fr} .kv{grid-template-columns:120px 1fr} }
    `}</style>
  );
}
